<template>
  <!-- Full-page notice for completely blocked countries -->
  <div v-if="type === 'country'" class="jbn-fullpage">
    <div class="jbn-fullpage-inner">
      <div class="jbn-icon jbn-icon--country">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
          <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" opacity="0.8"/>
        </svg>
      </div>
      <h2 class="jbn-fullpage-title">{{ t('jurisdiction.country_title') }}</h2>
      <p class="jbn-fullpage-desc">{{ resolvedMessage }}</p>
      <p class="jbn-fullpage-sub">
        {{ t('jurisdiction.contact_support') }}
      </p>
    </div>
  </div>

  <!-- Inline notices for game / payment / deposit_limit restrictions -->
  <div v-else class="jbn-notice" :class="'jbn-notice--' + type" role="alert">
    <div class="jbn-notice-icon">
      <!-- Game restriction icon -->
      <svg v-if="type === 'game'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
      </svg>
      <!-- Payment restriction icon -->
      <svg v-else-if="type === 'payment'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
      </svg>
      <!-- Deposit limit icon -->
      <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    </div>
    <div class="jbn-notice-content">
      <p class="jbn-notice-title">{{ noticeTitle }}</p>
      <p class="jbn-notice-message">{{ resolvedMessage }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

type NoticeType = 'game' | 'payment' | 'deposit_limit' | 'country'

const props = withDefaults(defineProps<{
  type: NoticeType
  message?: string
}>(), {
  message: undefined,
})

const defaultMessages = computed<Record<NoticeType, string>>(() => ({
  game: t('jurisdiction.game_default'),
  payment: t('jurisdiction.payment_default'),
  deposit_limit: t('jurisdiction.deposit_limit_default'),
  country: t('jurisdiction.country_default'),
}))

const noticeTitles = computed<Record<Exclude<NoticeType, 'country'>, string>>(() => ({
  game: t('jurisdiction.game_title'),
  payment: t('jurisdiction.payment_title'),
  deposit_limit: t('jurisdiction.deposit_limit_title'),
}))

const resolvedMessage = computed(() => props.message ?? defaultMessages.value[props.type])

const noticeTitle = computed(() => {
  if (props.type === 'country') return ''
  return noticeTitles.value[props.type]
})
</script>

<style scoped>
/* ── Full-page notice (type="country") ─────────────────────────── */
.jbn-fullpage {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 40px 24px;
  background: var(--bg-primary, #0f1923);
}

.jbn-fullpage-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 480px;
  gap: 16px;
}

.jbn-icon--country {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(237, 66, 69, 0.1);
  border: 1px solid rgba(237, 66, 69, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-danger, #ed4245);
}

.jbn-fullpage-title {
  font-family: var(--font-family-display, 'Russo One', sans-serif);
  font-size: clamp(18px, 3vw, 26px);
  font-weight: 700;
  color: var(--text-primary, #fff);
  line-height: 1.3;
  margin: 0;
}

.jbn-fullpage-desc {
  font-size: 15px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.65;
  margin: 0;
}

.jbn-fullpage-sub {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

/* ── Inline notice ─────────────────────────────────────────────── */
.jbn-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: var(--radius-md, 8px);
  border-width: 1px;
  border-style: solid;
}

/* Game restriction — neutral/info */
.jbn-notice--game {
  background: rgba(20, 117, 225, 0.07);
  border-color: rgba(20, 117, 225, 0.2);
  color: var(--accent-secondary, #1475e1);
}

/* Payment restriction — warning */
.jbn-notice--payment {
  background: rgba(255, 167, 38, 0.07);
  border-color: rgba(255, 167, 38, 0.2);
  color: var(--color-warning, #ffa726);
}

/* Deposit limit — warning */
.jbn-notice--deposit_limit {
  background: rgba(255, 167, 38, 0.07);
  border-color: rgba(255, 167, 38, 0.2);
  color: var(--color-warning, #ffa726);
}

.jbn-notice-icon {
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
}

.jbn-notice-content {
  flex: 1;
  min-width: 0;
}

.jbn-notice-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 3px 0;
  line-height: 1.4;
}

.jbn-notice-message {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
  margin: 0;
  line-height: 1.55;
}
</style>
